import React, { useEffect, useRef, useState } from 'react';
import {
	fitnessProfileFormErrorClassName,
	fitnessProfileModalBackdropClassName
} from './shared/fitnessProfileReadingStyles';
import {
	buildGoalsAddPayload,
	buildGoalsUpdatePayload,
	emptyGoalFormRow,
	goalToFormRow,
	validateGoalRows
} from '../../constants/fitnessProfileGoals';
import { todayIsoDate } from '../../utils/fitnessProfileBloodPressure';
import FitnessProfileGoalFormRow from './FitnessProfileGoalFormRow';

const MODE_CONFIG = {
	add: {
		title: 'Add goals',
		titleId: 'add-goals-modal-title',
		submitLabel: 'Save goals',
		savingLabel: 'Saving…',
		showAddRow: true,
		rowLabel: (index) => `Goal ${index + 1}`,
		requireAtLeastOne: true,
		buildPayload: buildGoalsAddPayload
	},
	manage: {
		title: 'Manage goals',
		titleId: 'goals-modal-title',
		submitLabel: 'Save changes',
		savingLabel: 'Saving…',
		showAddRow: false,
		rowLabel: () => 'Edit goal',
		requireAtLeastOne: false,
		buildPayload: buildGoalsUpdatePayload
	}
};

export default function FitnessProfileGoalsModal({
	mode,
	isOpen,
	goals = [],
	hasWeightReading = false,
	hasBloodPressureReading = false,
	onClose,
	onSave,
	saving,
	error
}) {
	const config = MODE_CONFIG[mode];
	const modalRef = useRef(null);
	const [rows, setRows] = useState([]);
	const [fieldErrors, setFieldErrors] = useState({});

	useEffect(() => {
		if (!isOpen || !config) return;

		if (mode === 'add') {
			setRows([emptyGoalFormRow()]);
		} else {
			setRows(goals.map(goalToFormRow));
		}
		setFieldErrors({});
	}, [isOpen, mode, goals, config]);

	useEffect(() => {
		function handleClickOutside(event) {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				onClose();
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}

		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen, onClose]);

	if (!isOpen || !config) return null;

	function onRowChange(index, field, value) {
		setRows((previous) => {
			const next = [...previous];
			next[index] = { ...next[index], [field]: value };
			return next;
		});
		if (fieldErrors[`goal_${field}_${index}`] || fieldErrors.goals) {
			setFieldErrors((previous) => {
				const next = { ...previous };
				delete next[`goal_${field}_${index}`];
				delete next.goals;
				return next;
			});
		}
	}

	function addRow() {
		setRows((previous) => [...previous, emptyGoalFormRow()]);
	}

	function removeRow(index) {
		setRows((previous) => {
			if (mode === 'add') {
				if (previous.length === 1) return [emptyGoalFormRow()];
				return previous.filter((_, rowIndex) => rowIndex !== index);
			}

			const row = previous[index];
			if (row.id) {
				const next = [...previous];
				next[index] = { ...row, _destroy: true };
				return next;
			}
			return previous.filter((_, rowIndex) => rowIndex !== index);
		});
	}

	function handleSubmit(event) {
		event.preventDefault();
		const errors = validateGoalRows(rows, {
			today: todayIsoDate(),
			requireAtLeastOne: config.requireAtLeastOne,
			existingGoals: goals,
			hasWeightReading,
			hasBloodPressureReading
		});
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		onSave(config.buildPayload(rows));
	}

	const visibleRows =
		mode === 'manage'
			? rows.map((row, index) => ({ row, index })).filter(({ row }) => !row._destroy)
			: rows.map((row, index) => ({ row, index }));

	return (
		<div className={fitnessProfileModalBackdropClassName}>
			<div
				ref={modalRef}
				className="relative flex flex-col w-full max-w-xl overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xl dark:border-dark-border dark:bg-dark-card dark:text-dark-text"
				style={{ maxHeight: '90vh' }}
				role="dialog"
				aria-modal="true"
				aria-labelledby={config.titleId}
			>
				<div className="relative shrink-0 border-b border-gray-200 px-8 pb-4 pt-6 pr-14 dark:border-dark-border">
					<h2 id={config.titleId} className="text-2xl font-bold text-gray-900 dark:text-dark-text">
						{config.title}
					</h2>
					<button
						type="button"
						className="absolute right-2 top-4 rounded-full p-2 transition duration-300 hover:bg-gray-200 dark:hover:bg-gray-700"
						onClick={onClose}
						aria-label="Close"
					>
						<svg
							className="h-6 w-6 text-gray-500 dark:text-gray-300"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
					<div className="min-h-0 flex-1 overflow-y-auto px-8 py-4">
						{error && (
							<p className={`mb-4 ${fitnessProfileFormErrorClassName}`} role="alert">
								{error}
							</p>
						)}
						{fieldErrors.goals && (
							<p className={`mb-4 ${fitnessProfileFormErrorClassName}`} role="alert">
								{fieldErrors.goals}
							</p>
						)}

						<div className="space-y-4">
							{visibleRows.map(({ row, index }) => (
								<FitnessProfileGoalFormRow
									key={row.id ? `goal-${row.id}` : `goal-${mode}-${index}`}
									row={row}
									index={index}
									fieldErrors={fieldErrors}
									onRowChange={onRowChange}
									onRemove={() => removeRow(index)}
									rowLabel={config.rowLabel(index)}
									showRemove={mode === 'add' ? rows.length > 1 : true}
								/>
							))}

							{config.showAddRow && (
								<button
									type="button"
									onClick={addRow}
									className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
								>
									+ Add another goal
								</button>
							)}
						</div>
					</div>

					<div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-200 px-8 py-4 dark:border-dark-border">
						<button
							type="button"
							onClick={onClose}
							className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={saving}
							className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-60"
						>
							{saving ? config.savingLabel : config.submitLabel}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
